// Dashboard Page - Premium dashboard with KPIs and charts
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { statsAPI, tripsAPI, expensesAPI } from '../services/api';
import { motion } from 'framer-motion';
import {
  Route, Receipt, DollarSign, TrendingUp, TrendingDown,
  MapPin, Calendar, AlertCircle, Plus, ArrowRight,
  Car, Fuel, Clock, Target, Navigation, Zap
} from 'lucide-react';
import Layout from '../components/Layout';
import { KPICard } from '../components/KPICard';
import GuidedTour from '../components/GuidedTour';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [runTour, setRunTour] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    loadDashboardData();
    // Onboarding is handled by FirstTripTutorial in App.js - no need for separate modal here
  }, []);

  const handleTourComplete = () => {
    setRunTour(false);
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, tripsRes, expensesRes] = await Promise.all([
        statsAPI.getDashboard(),
        tripsAPI.getAll({ skip: 0, limit: 5 }),
        expensesAPI.getAll({ skip: 0, limit: 5 })
      ]);
      setStats(statsRes.data);
      setRecentTrips(tripsRes.data);
      setRecentExpenses(expensesRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { EUR: '€', USD: '$', MAD: 'د.م.', SAR: 'ر.س' };
    return symbols[currency] || currency;
  };

  // Sample chart data (would come from API in production)
  const weeklyData = [
    { name: 'Lun', revenus: 120, km: 45 },
    { name: 'Mar', revenus: 180, km: 72 },
    { name: 'Mer', revenus: 150, km: 58 },
    { name: 'Jeu', revenus: 220, km: 89 },
    { name: 'Ven', revenus: 280, km: 112 },
    { name: 'Sam', revenus: 190, km: 76 },
    { name: 'Dim', revenus: 85, km: 34 },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="dashboard-loading">
          {/* Header Skeleton */}
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-10 w-32" />
          </div>
          {/* KPI Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          {/* Chart Skeleton */}
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </Layout>
    );
  }

  const currentMonth = stats?.current_month || {};
  const currency = stats?.currency || 'EUR';
  const currencySymbol = getCurrencySymbol(currency);
  const subscriptionPlan = stats?.subscription_plan || 'free';

  return (
    <Layout>
      <GuidedTour run={runTour} onComplete={handleTourComplete} />
      
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              Bonjour, {user?.first_name || user?.email?.split('@')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Voici un aperçu de votre activité ce mois-ci
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/trips')}
              className="gap-2"
              data-testid="new-trip-btn"
            >
              <Plus className="w-4 h-4" />
              Nouveau trajet
            </Button>
            <Button 
              onClick={() => navigate('/gps')}
              className="gap-2 bg-primary hover:bg-primary/90 shadow-button-primary"
              data-testid="start-gps-btn"
            >
              <Navigation className="w-4 h-4" />
              Démarrer GPS
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Revenus du mois"
            value={currentMonth.total_revenue || 0}
            prefix={currencySymbol}
            icon={DollarSign}
            color="success"
            change={12}
            changeLabel="vs mois dernier"
            delay={0}
          />
          <KPICard
            title="Dépenses"
            value={currentMonth.total_expenses || 0}
            prefix={currencySymbol}
            icon={Receipt}
            color="danger"
            change={-5}
            changeLabel="vs mois dernier"
            delay={0.1}
          />
          <KPICard
            title="Distance parcourue"
            value={(currentMonth.total_distance_km || 0).toFixed(0)}
            suffix=" km"
            icon={MapPin}
            color="blue"
            change={8}
            changeLabel="vs mois dernier"
            delay={0.2}
          />
          <KPICard
            title="Bénéfice net"
            value={currentMonth.net_profit || 0}
            prefix={currencySymbol}
            icon={TrendingUp}
            color={currentMonth.net_profit >= 0 ? 'success' : 'danger'}
            change={currentMonth.net_profit >= 0 ? 15 : -10}
            changeLabel="vs mois dernier"
            delay={0.3}
          />
        </div>

        {/* Subscription Alert */}
        {subscriptionPlan === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-amber-900 dark:text-amber-100">
                  Plan Gratuit — 3 trajets/mois
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                  Passez à Pro pour des trajets illimités, le scan IA et l'export comptable
                </p>
              </div>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0"
                onClick={() => navigate('/settings#subscription')}
                data-testid="upgrade-btn"
              >
                Passer à Pro
              </Button>
            </div>
          </motion.div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2 shadow-card border-border/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">Revenus de la semaine</CardTitle>
                  <CardDescription>Évolution de vos revenus sur 7 jours</CardDescription>
                </div>
                <Badge variant="secondary" className="font-normal">
                  +18% cette semaine
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weeklyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenu" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6B4CFF" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#6B4CFF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false} 
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [`${value}€`, 'Revenus']}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenus"
                      stroke="#6B4CFF"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenu)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="shadow-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Statistiques rapides</CardTitle>
              <CardDescription>Résumé du mois en cours</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Car className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Trajets</p>
                    <p className="text-xs text-muted-foreground">Ce mois</p>
                  </div>
                </div>
                <p className="text-2xl font-semibold tabular-nums">{currentMonth.trip_count || 0}</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                    <Fuel className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Carburant</p>
                    <p className="text-xs text-muted-foreground">Dépenses</p>
                  </div>
                </div>
                <p className="text-2xl font-semibold tabular-nums">{currencySymbol}0</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Temps total</p>
                    <p className="text-xs text-muted-foreground">En conduite</p>
                  </div>
                </div>
                <p className="text-2xl font-semibold tabular-nums">0h</p>
              </div>

              <Button 
                variant="outline" 
                className="w-full mt-2"
                onClick={() => navigate('/confidence')}
                data-testid="view-stats-btn"
              >
                Voir toutes les statistiques
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Trips */}
          <Card className="shadow-card border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Route className="w-5 h-5 text-primary" />
                    Derniers trajets
                  </CardTitle>
                  <CardDescription>Vos trajets récents</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/trips')}>
                  Voir tout
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentTrips.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Route className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <p className="text-muted-foreground mb-4">Aucun trajet enregistré</p>
                  <Button size="sm" onClick={() => navigate('/trips')} data-testid="create-trip-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un trajet
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentTrips.map((trip, index) => (
                    <motion.div
                      key={trip.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => navigate('/trips')}
                      data-testid={`trip-item-${index}`}
                    >
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {trip.departure_address} → {trip.arrival_address}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {new Date(trip.date).toLocaleDateString('fr-FR')}
                          <span>•</span>
                          <span>{trip.distance_km} km</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {currencySymbol}{trip.amount_charged}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Expenses */}
          <Card className="shadow-card border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-red-500" />
                    Dernières dépenses
                  </CardTitle>
                  <CardDescription>Vos dépenses récentes</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/expenses')}>
                  Voir tout
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentExpenses.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Receipt className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <p className="text-muted-foreground mb-4">Aucune dépense enregistrée</p>
                  <Button size="sm" onClick={() => navigate('/expenses')} data-testid="create-expense-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter une dépense
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentExpenses.map((expense, index) => (
                    <motion.div
                      key={expense.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => navigate('/expenses')}
                      data-testid={`expense-item-${index}`}
                    >
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Receipt className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          {t(`expenses.types.${expense.type}`) || expense.type}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <Calendar className="w-3 h-3" />
                          {new Date(expense.date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600 dark:text-red-400">
                          -{currencySymbol}{expense.amount}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
